import { keyBy } from 'lodash';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { v4 as uuid } from 'uuid';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  FetchableResourceType,
  ListOfResourcesToBeFetched,
  LoadAllocationsData,
  ResourceAllocations,
  ResourceType,
} from 'api/resource-management/types';
import { toastService } from 'components/molecules/toast';
import { toBulkActionPayload } from 'components/organisms/resource-management/utils/bulk-actions';
import { asApiZoomType } from 'components/organisms/resource-management/utils/common';
import {
  endDateParser,
  getCorrectedApiDateRangeBasedOnView,
  isDateBetweenRange,
  shiftDateIfOnNonWorkingDay,
  startDateParser,
} from 'components/organisms/resource-management/utils/dates';
import { isOverlapping } from 'components/organisms/resource-management/utils/events-conversion/common';
import { teamAllocationSummaryAsEvent } from 'components/organisms/resource-management/utils/events-conversion/resource-summary/team-summary';
import { userAllocationSummaryAsEvent } from 'components/organisms/resource-management/utils/events-conversion/resource-summary/user-summary';
import {
  resourceIdForExpandableRole,
  resourceIdForUser,
} from 'components/organisms/resource-management/utils/id';
import { useTranslation } from 'hooks/use-translation';
import {
  aggregationIdForUser,
  getAllocationAggregationKey,
} from 'queries/resource-management';
import { AllocationSummaryEvent, SchedulerEventType } from '../event-types';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { AggregationLookup, BulkActionPayloadMap, Range } from '../types';
import { BulkUpdateHandler, CommonManagerProps } from './types';

export const useAllocationStateManager = (
  props: CommonManagerProps,
): {
  bulkUpdate: BulkUpdateHandler;
  loadAllocations: <T extends ListOfResourcesToBeFetched>(
    props: LoadAllocationsData<T>,
  ) => Promise<ResourceAllocations>;
  sentRequestIds: string[];
} => {
  const {
    schedulerRef,
    currentViewPreset,
    workingDaysConfig,
    resourceManagementConfig,
    userLookup,
  } = props;
  const queryClient = useQueryClient();
  const [sentRequestIds, setSentRequestIds] = useState<string[]>([]);

  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');

  const bulkUpdate = useCallback(
    (actions: BulkActionPayloadMap) => {
      const requestId = uuid();

      setSentRequestIds((prev) => [...prev, requestId]);

      return ResourceManagmentApi.bulkAction(
        toBulkActionPayload(actions),
        requestId,
      )
        .then((response) => {
          const existingIds = [
            ...Object.keys(actions.update),
            ...Object.keys(actions.delete),
          ];
          const tempAllocationId = Object.keys(actions.create)[0];
          const createAllocationResponse = response.allocations.find(
            ({ allocation: createdAllocation }) =>
              !existingIds.includes(createdAllocation.id.toString()),
          );
          const tempEvent =
            schedulerRef.current.instance.eventStore.getById(tempAllocationId);

          if (tempEvent && createAllocationResponse) {
            tempEvent.id = createAllocationResponse.allocation.id;
          }

          return {
            processSummaryUpdate: true,
            processTotalAllocationUpdate: true,
          };
        })
        .catch(() => {
          const tempAllocationId = Object.keys(actions.create)[0];
          const tempEvent =
            schedulerRef.current.instance.eventStore.getById(tempAllocationId);

          if (tempEvent) {
            tempEvent.remove();
          }
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();

          return {
            processSummaryUpdate: true,
            processTotalAllocationUpdate: true,
          };
        });
    },
    [errorMessage, errorTitle, schedulerRef],
  );

  const updateExistingSummaryEvents = useCallback(
    (
      resourceId: number, // user or role Id
      oldEvents: AllocationSummaryEvent[],
      range: Range,
    ) => {
      const correctedStartDate = shiftDateIfOnNonWorkingDay(
        range.toDate.add(1, 'day').startOf('day'),
        workingDaysConfig,
      ).toDate();

      const correctedEndDate = shiftDateIfOnNonWorkingDay(
        range.fromDate.subtract(1, 'day').endOf('day'),
        workingDaysConfig,
        false,
      ).toDate();

      schedulerRef.current.instance.suspendRefresh();

      oldEvents.forEach((event) => {
        const startDateIsBetween = isDateBetweenRange(
          startDateParser(event.startDate),
          {
            startDate: range.fromDate,
            endDate: range.toDate,
          },
        );

        const endDateIsBetween = isDateBetweenRange(
          endDateParser(event.endDate),
          {
            startDate: range.fromDate,
            endDate: range.toDate,
          },
        );

        const isNewRangeInBetweenEvent =
          isDateBetweenRange(range.fromDate, {
            startDate: startDateParser(event.startDate),
            endDate: endDateParser(event.endDate),
          }) &&
          isDateBetweenRange(range.toDate, {
            startDate: startDateParser(event.startDate),
            endDate: endDateParser(event.endDate),
          });

        if (startDateIsBetween && endDateIsBetween) {
          schedulerRef.current.instance.eventStore.remove(event);
        } else if (isNewRangeInBetweenEvent) {
          // cut the existing event and create a new event with same details on the other side
          // create a new summary event for the other side of the range
          if (startDateIsBetween) {
            // startDate is same as range.fromDate; update the start date
            (event as any).setStartDate(correctedStartDate, false);
          } else if (endDateIsBetween) {
            // endDate is same as range.toDate; update the end date
            (event as any).setEndDate(correctedEndDate, false);
          } else {
            const newSummaryCommonProps = {
              startDate: correctedStartDate.toString(),
              endDate: event.endDate.toString(),
              forViewPreset: event.forViewPreset,
              allocatedMinutes: event.allocatedMinutes,
            };
            const newSummaryEvent: AllocationSummaryEvent =
              event.type === SchedulerEventType.UserAllocationSummary
                ? userAllocationSummaryAsEvent(
                    userLookup,
                    {
                      ...newSummaryCommonProps,
                      capacity: event.capacity,
                      userId: resourceId,
                    },
                    resourceManagementConfig?.softAllocations.enabled,
                  )
                : teamAllocationSummaryAsEvent(
                    {
                      ...newSummaryCommonProps,
                      roleId: resourceId,
                    },
                    resourceManagementConfig?.softAllocations.enabled,
                  );

            // cut short the existing event
            (event as any).setEndDate(correctedEndDate, false);

            schedulerRef.current.instance.eventStore.add(newSummaryEvent);
          }
        } else if (endDateIsBetween) {
          (event as any).setEndDate(correctedEndDate, false);
        } else if (startDateIsBetween) {
          (event as any).setStartDate(correctedStartDate, false);
        }
      });

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      resourceManagementConfig?.softAllocations.enabled,
      schedulerRef,
      userLookup,
      workingDaysConfig,
    ],
  );

  const loadAllocations = useCallback(
    ({ resources, range }: LoadAllocationsData<ListOfResourcesToBeFetched>) => {
      queryClient.setQueryDefaults(getAllocationAggregationKey(), {
        cacheTime: Infinity,
      });

      return new Promise<ResourceAllocations>((resolve, reject) => {
        const correctedRange = getCorrectedApiDateRangeBasedOnView(
          range,
          currentViewPreset,
          workingDaysConfig,
        );

        ResourceManagmentApi.getResourceAllocationsForPeople(
          asApiZoomType(currentViewPreset),
          {
            ...correctedRange,
            ...resources,
          },
        )
          .then((data) => {
            if (
              [ViewPresetId.Day, ViewPresetId.DayZoomed].includes(
                currentViewPreset,
              )
            ) {
              Object.keys(resources).forEach((key) => {
                if (resources[key].length > 0) {
                  resources[key].forEach((resourceId) => {
                    const resource =
                      schedulerRef.current.instance.resourceStore.getById(
                        key === FetchableResourceType.UserList
                          ? resourceIdForUser(resourceId)
                          : key === FetchableResourceType.RoleList
                          ? resourceIdForExpandableRole(resourceId)
                          : undefined,
                      );

                    if (resource != null) {
                      const overlappingEvents = resource.events.filter(
                        (event) =>
                          isOverlapping(
                            {
                              startDate: moment(event.startDate),
                              endDate: moment(event.endDate),
                            },
                            {
                              startDate: range.fromDate,
                              endDate: range.toDate,
                            },
                          ),
                      );

                      updateExistingSummaryEvents(
                        resourceId,
                        overlappingEvents,
                        range,
                      );
                    }
                  });
                }
              });
            }

            const resourceAllocations: ResourceAllocations = [];
            const aggregationLookup: AggregationLookup =
              queryClient.getQueryData<AggregationLookup>(
                getAllocationAggregationKey(),
              ) ?? {};

            data.resources.forEach((resource) => {
              const { type, aggregations } = resource;

              if (type === ResourceType.User) {
                resourceAllocations.push(resource);
                aggregationLookup[`user-${resource.user.userId}`] = {
                  ...aggregationLookup[`user-${resource.user.userId}`],
                  ...keyBy(
                    aggregations,
                    ({ projectId, roleId, referenceTeamId }) =>
                      aggregationIdForUser(projectId, roleId, referenceTeamId),
                  ),
                };
              } else {
                resourceAllocations.push(resource);
                aggregationLookup[`team-${resource.team.teamId}`] = {
                  ...aggregationLookup[`team-${resource.team.teamId}`],
                  ...keyBy(
                    aggregations,
                    ({ projectId }) => `project-${projectId}`,
                  ),
                };
              }
            });

            queryClient.setQueryData(
              getAllocationAggregationKey(),
              aggregationLookup,
            );

            resolve(resourceAllocations);
          })
          .catch((error) => {
            toastService({
              title: errorTitle,
              message: errorMessage,
            }).error();
            reject(error);
          });
      });
    },
    [
      currentViewPreset,
      errorMessage,
      errorTitle,
      queryClient,
      schedulerRef,
      updateExistingSummaryEvents,
      workingDaysConfig,
    ],
  );

  return {
    bulkUpdate,
    loadAllocations,
    sentRequestIds,
  };
};
